"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplet, Flame, Gauge, PhoneCall, ShieldAlert, Waves, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface PlumbingProblem {
  id: string;
  title: string;
  badge: string;
  description: string;
  symptom: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  link: string;
}
const problems: PlumbingProblem[] = [{
  id: "clogged-drains",
  title: "Clogged Drains",
  badge: "Most Common",
  description: "Slow-draining sinks, standing shower water, and foul drain odors requiring motorized augers or hydro jetting.",
  symptom: "Water pooling in sinks, tubs, or floor drains",
  icon: Droplet,
  link: "/drain-problems/"
}, {
  id: "water-leaks",
  title: "Water Leaks",
  badge: "Urgent",
  description: "Hidden slab leaks, dripping pipe joints, and wall moisture that drive up water bills and cause structural damage.",
  symptom: "Unexplained high water bills or damp drywall",
  icon: Waves,
  link: "/water-leak-problems/"
}, {
  id: "no-hot-water",
  title: "No Hot Water",
  badge: "Same-Day Fix",
  description: "Failing heating elements, sediment buildup, pilot light issues, or leaking storage and tankless water heaters.",
  symptom: "Lukewarm water, total cold flow, or tank rumbling",
  icon: Flame,
  link: "/water-heater-problems/"
}, {
  id: "toilet-problems",
  title: "Toilet Breakdowns",
  badge: "High Priority",
  description: "Constantly running tanks, stubborn clogs, weak flushes, and base leaks that risk bathroom flooring.",
  symptom: "Frequent overflows or phantom flushing cycles",
  icon: Wrench,
  link: "/toilet-problems/"
}, {
  id: "low-water-pressure",
  title: "Low Water Pressure",
  badge: "Diagnostics",
  description: "Mineral-clogged aerators, main line leaks, failed pressure reducing valves, or corroded distribution pipes.",
  symptom: "Weak flow across multiple faucets or showers",
  icon: Gauge,
  link: "/water-pressure-problems/"
}, {
  id: "sewer-backup",
  title: "Sewer Line Backups",
  badge: "Biohazard Emergency",
  description: "Tree root infiltration, collapsed lateral pipes, and hazardous waste backups affecting all home plumbing fixtures.",
  symptom: "Gurgling drains and foul sewage odors indoors",
  icon: ShieldAlert,
  link: "/sewer-line-repair/"
}, {
  id: "burst-pipes",
  title: "Burst & Cracked Pipes",
  badge: "Immediate Dispatch",
  description: "Severe water line ruptures due to sudden pressure spikes, freezing, or aging metal requiring immediate isolation.",
  symptom: "Active spraying water or flooding inside walls",
  icon: AlertTriangle,
  link: "/pipe-problems/"
}];
export default function PlumbingProblems() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="commonplumbingproblems" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-3">
            Jonesboro Diagnostic Guide
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Common Plumbing Problems We Fix Fast
          </h2>
          <p className="mt-4 font-sans text-base text-muted-foreground sm:text-lg">
            From sudden pipe ruptures to stubborn sewer blockages, Jonesboro Emergency Plumber diagnoses and resolves critical home plumbing issues 24 hours a day.
          </p>
        </div>

        {/* Problems Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {problems.map((problem, i) => {
          const Icon = problem.icon;
          const isFeatured = i === problems.length - 1; // Highlight burst pipes emergency card

          return <motion.div key={problem.id} data-index={i} variants={itemVariants} className={`flex ${isFeatured ? "md:col-span-2 lg:col-span-1" : ""}`}>
                <Card className="group flex h-full w-full flex-col justify-between border-border bg-card shadow-xs transition-shadow duration-200 hover:shadow-md">
                  <div>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex size-10 items-center justify-center rounded-lg bg-secondary text-primary">
                          <Icon className="size-5" />
                        </div>
                        <Badge variant="secondary" className="font-medium text-xs">
                          {problem.badge}
                        </Badge>
                      </div>
                      <CardTitle className="mt-4 text-xl font-bold text-card-foreground">
                        {problem.title}
                      </CardTitle>
                      <CardDescription className="text-sm font-normal text-muted-foreground">
                        {problem.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-0">
                      <div className="rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
                        <span className="font-semibold text-foreground">Common sign: </span>
                        {problem.symptom}
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2">
                    <Button asChild variant="ghost" size="sm" className="w-full justify-between px-2 text-primary hover:bg-secondary/50 hover:text-primary">
                      <Link to={problem.link} className="flex items-center justify-between w-full">
                        <span>Learn how we fix this</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Call to Action Banner */}
        <div className="mt-12 rounded-xl border border-border bg-secondary/50 p-6 sm:p-8 md:mt-16">
          <div className="flex flex-col items-center justify-between gap-6 text-center md:flex-row md:text-left">
            <div className="max-w-2xl">
              <h3 className="font-default text-xl font-bold text-foreground sm:text-2xl">
                Unsure what plumbing issue you are facing?
              </h3>
              <p className="mt-1 text-sm text-muted-foreground sm:text-base">
                Our licensed Jonesboro plumbers provide quick troubleshooting and immediate 24/7 on-site emergency dispatch.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button asChild variant="outline" size="lg">
                <Link to="/problems/">
                  View All Problems
                </Link>
              </Button>
              <Button asChild variant="default" size="lg" className="gap-2">
                <Link to="/contact/">
                  <PhoneCall className="size-4" />
                  <span>Request Emergency Help</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}